import styled from 'threads5/styles/styled';
import Section from 'src/components-v2/Layout/Section/Section';
import { VideoPlayer } from 'src/components-v2/Media/VideoPlayer';
import { AnimatedVideo } from 'src/components-v2/Media';
import Image from 'src/components-v2/Media/Image/Image';

export const RichTextWrapper = styled(Section)(() => {
  return {
    '& h3': {
      marginBottom: '16px',
    },
    '& p:has(+ a)': {
      marginBottom: '32px',
    },
    '& p:has(.decorative-text)': {
      marginBottom: '16px',
    },
    '& p:nth-last-of-type(1):empty': {
      display: 'none',
    },
  };
});

export const Video = styled(VideoPlayer, {
  // Don't filter `isLazy`: VideoPlayer consumes it. Filtering left the player
  // at its default `isLazy = true`, so the CMS field had no effect and
  // below-the-fold videos stayed an empty lazyload placeholder.
  shouldForwardProp: (prop) => {
    return prop !== 'isDarkBackground';
  },
})<{ isDarkBackground: boolean; isLazy: boolean }>(
  ({ theme, isDarkBackground }) => {
    return {
      marginBottom: '20px',
      marginX: '-1px',
      border: isDarkBackground ? '1px solid white' : '1px solid black',
      height: '245px',
      [theme.breakpoints.up('sm')]: {
        marginBottom: '25px',
        height: '350px',
      },
      [theme.breakpoints.up('md')]: {
        height: '425px',
      },
      [theme.breakpoints.up('lg')]: {
        marginBottom: '0',
      },
    };
  },
);

export const ColumnWrapper = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'isReversed' && prop !== 'isDarkBackground';
  },
})<{
  isReversed: boolean;
  isDarkBackground: boolean;
}>(({ theme, isReversed, isDarkBackground }) => {
  return {
    display: 'flex',
    flexDirection: isReversed ? 'column-reverse' : 'column',
    alignItems: 'center',
    border: isDarkBackground
      ? '1px solid white'
      : `1px solid ${theme.palette['base/black/600']}`,
    borderRadius: '8px',
    padding: '40px 0 40px 0',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('lg')]: {
      alignItems: 'center',
    },
  };
});

export const EmbeddedVideoPlayer = styled(VideoPlayer)<{ playing: boolean }>(
  ({ playing }) => {
    return {
      height: '100%',
      zIndex: playing ? 3 : 0,
      width: '100%',
      position: 'absolute',
    };
  },
);

export const VideoCardShadow = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'rainbowGlowEffect';
  },
})<{ rainbowGlowEffect: boolean }>(({ rainbowGlowEffect }) => {
  return {
    position: 'relative',
    width: '100%',
    ...(rainbowGlowEffect && {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '150%',
        height: '150%',
        background:
          'url(https://images.ctfassets.net/ss5kfr270og3/yW85aleeHWAyUvSpkNw4y/9a41c9ae46dd079dce69729ad9b480d2/AdobeStock_566188450__8_.png?q=1&w=1470&h=871&fm=webp)',
        backgroundSize: '72% 72%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        zIndex: 0,
      },
    }),
  };
});

export const VideoCardWrapper = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'isVideoPlaying';
  },
})<{ isVideoPlaying?: boolean }>(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: '20px',
    minHeight: '415px',
    [theme.breakpoints.up('sm')]: {
      minHeight: '770px',
    },
    padding: '0',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
    '& a, & button': {
      '&::after': {
        content: '""',
        position: 'fixed',
        display: 'block',
        top: '-222%',
        left: '0',
        width: '100%',
        height: '330%',
      },
    },
    '& .btn': {
      alignSelf: 'flex-start',
      width: '100%',
      justifyContent: 'center',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
        justifyContent: 'left',
      },
    },
    '& p:nth-last-of-type(1):empty': {
      display: 'none',
    },
    '&:hover': {
      '& .light-mode-text-stack-background': {
        opacity: theme.palette.mode === 'light' ? 1 : 0,
      },
    },
  };
});

export const DarkModeGradientOverlay = styled('div')(({ theme }) => {
  return {
    position: 'absolute',
    zIndex: 1,
    background: theme.palette['web3/component/video-card/gradient-overlay'],
    inset: 0,
  };
});

export const CoverVideo = styled(AnimatedVideo)(() => {
  return {
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    objectFit: 'cover',
  };
});

export const CoverImage = styled(Image)(() => {
  return {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };
});

export const TextStack = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    zIndex: 2,
    padding: '40px 30px',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    background: theme.palette['web3/component/video-card/background-resting'],
    backdropFilter: theme.palette.mode === 'light' ? 'blur(15px)' : 'none',
    transform: 'translateZ(0px)', // GPU acceleration hack
    gap: theme.spacing(2.25),
    [theme.breakpoints.up('sm')]: {
      gap: theme.spacing(4),
      padding: '70px 64px',
    },
    [theme.breakpoints.up('md')]: {
      padding: '70px',
    },
  };
});

export const LightModeTextStackBackground = styled('div')(({ theme }) => {
  return {
    background:
      theme.palette['web3/component/video-card/text-stack-background'],
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: '100%',
    width: '100%',
    zIndex: -1,
    opacity: 0,
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    transition: 'opacity 0.66s ease-in-out',
  };
});
